import { Pagination } from '@/api';
import { CreateTeamData, FindTeamsOptions, TeamAPI, TeamMember, UpdateTeamData } from '@/api/team';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export function useTeams(options?: FindTeamsOptions) {
  const { workgroupEpoch } = useAuth();
  const [data, setData] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>();
  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await TeamAPI.getAll(options);

      if (response.code === '200' && response.data) {
        setData(response.data as any);

         setPagination({
           totalItems: response.totalNoOfItem || 0,
           page: response.page || 1,
           perPage: response.perPage || 10,
         });
      } else {
        setError(response.message || 'Failed to fetch teams');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const createTeam = async (teamData: CreateTeamData) => {
    try {
      setLoading(true);
      const response = await TeamAPI.create(teamData);

      if (response.code === '200' || response.code === '201') {
        await fetchTeams();
        return { success: true, data: response.data, message: 'Team created successfully' };
      } else {
        return { success: false, message: response.message || 'Failed to create team' };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const updateTeam = async (id: number, teamData: UpdateTeamData) => {
    try {
      setLoading(true);
      const response = await TeamAPI.update(id, teamData);

      if (response.code === '200') {
        await fetchTeams();
        return { success: true, data: response.data, message: 'Team updated successfully' };
      } else {
        return { success: false, message: response.message || 'Failed to update team' };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const deleteTeam = async (id: number) => {
    try {
      setLoading(true);
      const response = await TeamAPI.delete(id);

      if (response.code === '200') {
        await fetchTeams();
        return { success: true, message: 'Team deleted successfully' };
      } else {
        return { success: false, message: response.message || 'Failed to delete team' };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const updateMemberRole = async (memberId: number, roleId: number) => {
    try {
      const response = await TeamAPI.updateMemberRole(memberId, roleId);

      if (response.code === '200') {
        await fetchTeams();
        return { success: true, message: 'Member role updated successfully' };
      } else {
        return { success: false, message: response.message || 'Failed to update member role' };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      return { success: false, message: errorMessage };
    }
  };

  const updateMemberPermissions = async (memberId: number, permissions: any[]) => {
    try {
      const response = await TeamAPI.updateMemberPermissions(memberId, permissions);

      if (response.code === '200') {
        await fetchTeams();
        return { success: true, message: 'Member permissions updated successfully' };
      } else {
        return { success: false, message: response.message || 'Failed to update member permissions' };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      return { success: false, message: errorMessage };
    }
  };

  const deactivateMember = async (memberId: number) => {
    try {
      const response = await TeamAPI.deactivateMember(memberId);

      if (response.code === '200') {
        await fetchTeams();
        return { success: true, message: 'Member deactivated successfully' };
      } else {
        return { success: false, message: response.message || 'Failed to deactivate member' };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      return { success: false, message: errorMessage };
    }
  };

  const activateMember = async (memberId: number) => {
    try {
      const response = await TeamAPI.activateMember(memberId);

      if (response.code === '200') {
        await fetchTeams();
        return { success: true, message: 'Member activated successfully' };
      } else {
        return { success: false, message: response.message || 'Failed to activate member' };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      return { success: false, message: errorMessage };
    }
  };

  const resetMemberPassword = async (memberId: number) => {
    try {
      const response = await TeamAPI.resetMemberPassword(memberId);

      if (response.code === '200' && response.data) {
        return {
          success: true,
          message: 'Password reset successfully',
          tempPassword: response.data.tempPassword
        };
      } else {
        return { success: false, message: response.message || 'Failed to reset password' };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      return { success: false, message: errorMessage };
    }
  };

  const deleteMember = async (memberId: number) => {
    try {
      const response = await TeamAPI.deleteMember(memberId);

      if (response.code === '200') {
        await fetchTeams();
        return { success: true, message: 'Member account deleted successfully' };
      } else {
        return { success: false, message: response.message || 'Failed to delete member account' };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      return { success: false, message: errorMessage };
    }
  };

  useEffect(() => {
    fetchTeams();
  },  [options?.workgroupId, workgroupEpoch]);

  return {
    data,
    loading,
    error,
    pagination,
    createTeam,
    updateTeam,
    deleteTeam,
    updateMemberRole,
    updateMemberPermissions,
    deactivateMember,
    activateMember,
    resetMemberPassword,
    deleteMember,
    refetch: fetchTeams
  };
}

export function useTeamMember(id: number) {
  const [data, setData] = useState<TeamMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTeamMember = async () => {
      try {
        setLoading(true);
        const response = await TeamAPI.getById(id);

        if (response.code === '200' && response.data) {
          setData(response.data);
        } else {
          setError(response.message || 'Failed to fetch team member');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTeamMember();
    }
  }, [id]);

  return {
    data,
    loading,
    error,
  };
}

export default useTeams;
